import {
    BATCHED_ACTIONS} from './constants.js';
import {isAdditiveType, pruneRedundantActions, isPrunable} from './pruneRedundantActions.js';
import {uuid} from 'uuidv4';

export default superclass => class extends superclass  {
    executedActions = [];
    undoneActions = [];
    queuedActions = [];
    queuedContentIds = [];

    async logAction(action) {
        let id = uuid();

        // log the action in RoomActionsManager (to be later sent
        // to other people in the room)
        // Keep track of whether we had to prune actions together
        // when enqueing
        let _action = this.getAction(action);
        let didPrune = 
                await this._logAction(id, _action, action);

        action = {
            ... action,
            payload: {
                ... action.payload,
            },
            contentId: _action.getContentId(action)
        };

        if (isAdditiveType(action.type)) {
            // Some actions are additive, like MOVE_STEPS. For example
            // 2 subsequent MOVE_STEPS can be combined into 1 MOVE_STEPS,
            // if they refer to the same step tooggle

            let {lastAction} = this;

            if (lastAction === undefined) {
                // This is the first action in the queue, so we don't
                // need to add them together
                this.executedActions.push(action);
            } else {
                // first get the last enqueued action from RoomActionsManager
                // This may have been added with previous actions
                let deserializedAction = this.getAction(
                    lastAction.operation)
                    .deserialize(lastAction.operation);

                if (didPrune) { 
                    // if we did prune/add, then we should replace the last undo action
                    // with this. This is equivalent to when you have two subequent
                    // MOVE_STEPS. We want to replace the last undo with the new
                    // added MOVE_STEPS action (i.e. the deserializedAction variable)
                    this.executedActions[this.executedActions.length - 1] = deserializedAction;
                } else {
                    // we did not prune/add, so we just need to add it to the undo list
                    this.executedActions.push(deserializedAction);
                }
            }
        } else if (didPrune) {
            // if it's not additive, but was pruned, then we need to take the
            // savedValue of the last action and use that value here in the
            // new action.
            // E.g. A knob value = 0. The user changes to 0.5 then 1. If they undo,
            // it should go to 0, not 0.5.
            this.executedActions[this.executedActions.length - 1] = this.combineWithLast(action);
        } else {
            // otherwise we just add it to the list of actions that can be undone
            this.executedActions.push({...action, uuid: id});
        }

        // make sure the ids stay in sync
        this.executedActions[this.executedActions.length - 1].uuid =
            this.lastAction ?
            this.lastAction.operation.uuid :
            id;

        // this.updateUndoRedux();
    }

    /**
     * Removes any immediately preceeding actions
     * of a given action type, that are redundant and 
     * can entirely replaced by this action
     */
    undo() {
        if (this.executedActions.length > 0) {
            let actionToUndo = this.executedActions.pop();
            this.undoAction(actionToUndo, false);
            this.undoneActions.push(actionToUndo);
            if (BATCHED_ACTIONS.indexOf(actionToUndo.type) > -1) {
                let nextToUndo = this.executedActions[this.executedActions.length - 1];
                if (nextToUndo === undefined) {
                    this.sequencerEngine.updateTracks(true);
                    return;
                }

                if (this.shouldAlsoUndo(actionToUndo, nextToUndo)) {
                    this.undo();
                } else {
                    // theres no undo actions left so update redux
                    this.sequencerEngine.updateTracks(true);
                }
            } else {
                console.log("NOTE BATCHED SO UPDATE");
                this.sequencerEngine.updateTracks(true);
            }
        }
    }

    undoAction(action, updateRedux=true) {
        let actionToUndo = this.getAction(action);
        if (actionToUndo === undefined) {
            return {};
        }
        if (this.roomActionsManager) {
            this.roomActionsManager.removeActionWithId(action.uuid);
        }
        return actionToUndo.undo(action, updateRedux);
    }

    shouldAlsoUndo(actionToUndo, nextToUndo) {
        if (BATCHED_ACTIONS.indexOf(nextToUndo.type) === -1) {
            return false;
        }
        return Math.abs(actionToUndo.timestamp - nextToUndo.timestamp) < 5000;
    }
        
    redo() {
        if (this.undoneActions.length > 0) {
            let actionToRedo = this.undoneActions.pop();
            this.runAction(actionToRedo);
            this.logAction(actionToRedo);
            this.sequencerEngine.updateTracks(true);
        }
    }

    async _logAction(id, actionRunner, action) {
        let serialized = await actionRunner.serialize(action, id);
        if (serialized) {
            let oldLen = this.queuedActions.length;
            this.queuedActions = pruneRedundantActions(
                serialized,
                this.queuedActions);
            let newLength = this.queuedActions.length;
            this.updateQueuedContentIds();
            this.queuedActions[this.queuedActions.length - 1].operation.uuid = id;
            return newLength === oldLen;
        }
        return false;
    }
    

    updateQueuedContentIds() {
        let queuedContentIds = new Set();
        for (let i in this.queuedActions) {
            let {operation}= this.queuedActions[i];
            if (Array.isArray(operation.contentId)) {
                operation.contentId.forEach(id => queuedContentIds.add(id));
            } else {
                queuedContentIds.add(operation.contentId);
            }
        }

        if (queuedContentIds.size === this.queuedContentIds.length) {
            // no need to update
            return;
        }

        this.queuedContentIds = Array.from(queuedContentIds);
    }

    combineWithLast(action) {
        let lastAction = this.executedActions[this.executedActions.length - 1];
        if (lastAction && lastAction.payload.savedValue !== undefined) {
            action.payload.savedValue = lastAction.payload.savedValue;
        }
        return action;
    }

    get lastAction() {
        return this.queuedActions[this.queuedActions.length - 1];
    }
}

